import type { PrototypeRuntime, Root } from './prototypeTypes';
import type { PrototypeState } from './controllerState';
import { renderPagedInfiniteList } from './progressiveList';
import { activateCommunityTab, communityHydrationKey, getCommunityMountState, getCommunityTabState, isCommunityTab, resolveCommunityMount, type CommunityMountState, type CommunityTab } from './communityMountState';
import { renderCommunityBuiltTopicCard, renderCommunityIdeaListItem, type CommunityListItem } from './communityIdeaListRenderer';
import { renderEmptyStateMarkup } from './emptyStateMarkup';
import { renderCommunityLoadError } from './communityLoadError';
import { prefetchTurnstileToken } from './turnstileRuntime';
import { shouldCommitTextInputOnEnter } from './inputComposition';
import { paintCommunityVoteAllowance, type CommunityVoteAllowance } from './communityVoteAllowance';

type CommunityListResponse = {
	ideas?: CommunityListItem[];
	hasMore?: boolean;
	nextCursor?: string | null;
	windowEnd?: string;
	viewerVoteAllowance?: CommunityVoteAllowance;
};

export async function hydrateCommunityView(root: Root, runtime: PrototypeRuntime, state: PrototypeState, opts?: { scope?: HTMLElement }) {
	const scope = resolveCommunityMount(root, opts?.scope);
	if (!scope) return;
	const mountState = getCommunityMountState(scope, runtime);
	if (!mountState || !scope.querySelector('[data-community-role="idea-list"]')) return;
	const selectedTab = scope.querySelector<HTMLElement>('[data-community-role="filters"] .category-pill.active')?.dataset.communityFilter;
	if (isCommunityTab(selectedTab)) { mountState.activeTab = selectedTab; mountState.filter = selectedTab; }
	mountState.tabs[mountState.activeTab].search = (scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]')?.value ?? mountState.tabs[mountState.activeTab].search).trim();
	activateCommunityTab(scope, mountState.activeTab);
	paintCommunityVoteAllowance(scope, { isLoggedIn: state.isLoggedIn });
	const { shouldHydrate } = await import('./hydrateGuard');
	if (!shouldHydrate(communityHydrationKey(scope))) return;
	// Community hydration crosses an async import above. A visitor can enter the
	// page and be login-gated while that import is pending; in that ordering the
	// auth modal has already cancelled the old warm-up before it exists. Never
	// start a vote token warm-up for a visitor, an inactive Community mount, or
	// while any modal owns the foreground.
	if (state.isLoggedIn && canPrefetchCommunityVote(scope)) prefetchTurnstileToken(runtime, 'community-idea-vote');
	wireCommunitySearch(scope, mountState);
	const reset = mountState.tabs[mountState.activeTab].items.length === 0;
	if (reset) mountState.tabs[mountState.activeTab].loadAbortController?.abort();
	await fetchAndRenderCommunityIdeas(scope, mountState, mountState.activeTab, { reset });
}

function canPrefetchCommunityVote(scope: HTMLElement): boolean {
	if (!scope.isConnected) return false;
	const communityIsActive = scope.closest('#view-community.active, #view-social.active .social-panel.active') !== null;
	if (!communityIsActive) return false;
	return document.querySelector('.modal-overlay.active:not([hidden])') === null;
}

async function fetchAndRenderCommunityIdeas(scope: HTMLElement, state: CommunityMountState, tab: CommunityTab, options: { reset: boolean }): Promise<void> {
	const tabState = getCommunityTabState(state, tab);
	const list = scope.querySelector<HTMLElement>(`[data-community-role="tab-panel"][data-community-tab-panel="${tab}"] [data-community-role="idea-list"]`) ?? scope.querySelector<HTMLElement>('[data-community-role="idea-list"]');
	if (!list) return;
	if (state.activeTab !== tab) return;
	const controller = new AbortController();
	tabState.loadAbortController = controller;
	const generation = ++tabState.loadGeneration;
	if (options.reset) { tabState.items = []; tabState.nextCursor = null; tabState.windowEnd = undefined; tabState.error = null; tabState.restoreScrollOnNextPaint = false; }
	// A populated cached tab with a saved position is the restore fact. Keep it
	// local before rendering mutates the tab state, rather than depending on an
	// incidental one-shot flag surviving the async render boundary.
	const restoreCachedScroll = !options.reset && tabState.items.length > 0 && tabState.scrollTop > 0;
	tabState.restoreScrollOnNextPaint = false;
	let restoreScrollPending = restoreCachedScroll;
	const initialPage = !options.reset && tabState.items.length > 0
		? { items: tabState.items, hasMore: Boolean(tabState.nextCursor), nextCursor: tabState.nextCursor }
		: undefined;
	list.classList.toggle('topic-grid', tab === 'completed');
	list.classList.toggle('idea-list', tab === 'recent');
	freezeListHeight(list);
	renderPagedInfiniteList({
		container: list,
		initialPage,
		loadPage: async (cursor) => {
			const response = await state.runtime.api.listCommunityIdeas(tab === 'recent' ? 'recent' : 'completed', { limit: 20, cursor, search: tabState.search }, controller.signal) as CommunityListResponse;
			if (!isCurrentLoad(state, tab, generation, controller)) throw new DOMException('Aborted', 'AbortError');
			if (response.windowEnd) tabState.windowEnd = response.windowEnd;
			paintCommunityVoteAllowance(scope, {
				isLoggedIn: response.viewerVoteAllowance !== undefined,
				allowance: response.viewerVoteAllowance,
			});
			tabState.nextCursor = response.nextCursor ?? null;
			return { items: response.ideas ?? [], hasMore: response.hasMore, nextCursor: response.nextCursor ?? null };
		},
		renderItem: (idea) => tab === 'recent' ? renderCommunityIdeaListItem(idea) : renderCommunityBuiltTopicCard(idea),
		skeletonClassName: tab === 'completed' ? 'topic-card-compact-skeleton' : 'community-skeleton-card',
		skeletonCount: 3,
		endMessage: tab === 'recent' ? "You've reached the end of recent community picks." : "You've reached the end of built topics.",
		emptyElement: renderEmptyState(tab),
		renderError: (_error, retry) => {
			if (!isCurrentLoad(state, tab, generation, controller)) return null;
			tabState.error = 'load_failed'; releaseListHeight(list); return renderCommunityLoadError(retry);
		},
		onItemsChange: (items) => {
			if (!isCurrentLoad(state, tab, generation, controller)) return;
		tabState.items = items; tabState.error = null; releaseListHeight(list); if (restoreScrollPending) { restoreScrollPending = false; restoreCommunityWindowScroll(state, tab, generation, controller, tabState); }
		},
	});
}

function isCurrentLoad(state: CommunityMountState, tab: CommunityTab, generation: number, controller: AbortController): boolean {
	const current = state.tabs[tab];
	return state.activeTab === tab && current.loadGeneration === generation && current.loadAbortController === controller && !controller.signal.aborted;
}

function renderEmptyState(tab: CommunityTab): HTMLElement {
	const template = document.createElement('template');
	template.innerHTML = renderEmptyStateMarkup(tab === 'recent'
		? { icon: 'message', title: 'No recent community picks', subtitle: 'Check back soon for new requests to vote on.', hostClass: 'community-empty-state' }
		: { icon: 'book', title: 'No completed topics yet', subtitle: 'Selected requests will appear here once they become topics.', hostClass: 'community-empty-state' });
	return template.content.firstElementChild as HTMLElement;
}

function freezeListHeight(list: HTMLElement): void { if (list.offsetHeight > 0) list.style.minHeight = `${list.offsetHeight}px`; }
function releaseListHeight(list: HTMLElement): void { list.style.minHeight = ''; }
function restoreCommunityWindowScroll(state: CommunityMountState, tab: CommunityTab, generation: number, controller: AbortController, tabState: { scrollTop: number }): void {
	if (tabState.scrollTop <= 0 || typeof window === 'undefined') return;
	requestAnimationFrame(() => requestAnimationFrame(() => {
		if (!isCurrentLoad(state, tab, generation, controller)) return;
		window.scrollTo({ top: tabState.scrollTop, behavior: 'auto' });
	}));
}

function wireCommunitySearch(scope: HTMLElement, state: CommunityMountState): void {
	const input = scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]');
	if (!input || input.dataset.bound === 'true') return;
	input.dataset.bound = 'true';
	input.addEventListener('input', () => toggleCommunityClearButton(scope));
	input.addEventListener('keydown', (event) => { if (shouldCommitTextInputOnEnter(event)) { event.preventDefault(); runCommunitySearchInternal(scope, state); } });
}

function currentCommunitySearch(scope: HTMLElement): string { return (scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]')?.value ?? '').trim(); }
function toggleCommunityClearButton(scope: HTMLElement): void { const input = scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]'); const clear = scope.querySelector<HTMLButtonElement>('[data-community-role="search-clear"]'); if (input && clear) clear.style.display = input.value.trim() ? '' : 'none'; }
function runCommunitySearchInternal(scope: HTMLElement, state: CommunityMountState): void { const tab = state.activeTab; const tabState = state.tabs[tab]; tabState.search = currentCommunitySearch(scope); tabState.loadAbortController?.abort(); toggleCommunityClearButton(scope); void fetchAndRenderCommunityIdeas(scope, state, tab, { reset: true }); }

export function runCommunitySearch(root: Root, source?: HTMLElement): void { const scope = resolveCommunityMount(root, source); const state = scope ? getCommunityMountState(scope) : null; if (scope && state) runCommunitySearchInternal(scope, state); }
export function clearCommunitySearch(root: Root, source?: HTMLElement): void { const scope = resolveCommunityMount(root, source); const state = scope ? getCommunityMountState(scope) : null; if (!scope || !state) return; const input = scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]'); if (input) input.value = ''; runCommunitySearchInternal(scope, state); }

export function applyCommunityListFilter(root: Root, filter?: string, source?: HTMLElement): void {
	const scope = resolveCommunityMount(root, source); const state = scope ? getCommunityMountState(scope) : null;
	if (!scope || !state) return;
	const tab: CommunityTab = isCommunityTab(filter) ? filter : 'recent';
	const outgoing = state.tabs[state.activeTab];
	outgoing.scrollTop = typeof window === 'undefined' ? 0 : window.scrollY; outgoing.loadAbortController?.abort();
	state.activeTab = tab; state.filter = tab;
	state.tabs[tab].restoreScrollOnNextPaint = state.tabs[tab].items.length > 0;
	activateCommunityTab(scope, tab);
	const input = scope.querySelector<HTMLInputElement>('[data-community-role="search-input"]'); if (input) input.value = state.tabs[tab].search;
	toggleCommunityClearButton(scope);
	void fetchAndRenderCommunityIdeas(scope, state, tab, { reset: false });
}
